import React, { useState, useEffect } from "react";
import { Input, Select, Space } from "antd";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  CountryCode,
} from "libphonenumber-js";
import ReactCountryFlag from "react-country-flag";

const countryNames =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

interface DialOption {
  iso: CountryCode;
  name: string;
  dialCode: string;
}

// When multiple countries share a dial code, keep the most prominent one
const PREFERRED_ISO_FOR_CODE: Record<string, CountryCode> = {
  "+1":   "US",
  "+7":   "RU",
  "+39":  "IT",
  "+44":  "GB",
  "+47":  "NO",
  "+61":  "AU",
  "+212": "MA",
  "+262": "RE",
  "+290": "SH",
  "+358": "FI",
  "+590": "GP",
  "+599": "CW",
};

const dialOptions: DialOption[] = (() => {
  const seen = new Map<string, DialOption>();
  getCountries().forEach((iso) => {
    const dialCode = `+${getCountryCallingCode(iso)}`;
    const entry: DialOption = { iso, name: countryNames?.of(iso) ?? iso, dialCode };
    const existing = seen.get(dialCode);
    if (!existing || PREFERRED_ISO_FOR_CODE[dialCode] === iso) {
      seen.set(dialCode, entry);
    }
  });
  return Array.from(seen.values()).sort((a, b) => {
    if (a.iso === "MY") return -1;
    if (b.iso === "MY") return 1;
    if (a.iso === "SG") return -1;
    if (b.iso === "SG") return 1;
    return a.name.localeCompare(b.name);
  });
})();

const DEFAULT_ISO: CountryCode = "MY";
const DEFAULT_DIAL_CODE = "+60";

interface PhoneNumberInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({ value, onChange, disabled }) => {
  const [iso, setIso] = useState<CountryCode>(DEFAULT_ISO);
  const [dialCode, setDialCode] = useState<string>(DEFAULT_DIAL_CODE);
  const [inputNumber, setInputNumber] = useState<string>("");

  // Sync only when value is a complete, parseable number (API prefill / form.setFieldValue).
  // If value is incomplete (mid-typing), parsePhoneNumberFromString returns undefined
  // and we deliberately leave local state untouched so the input isn't wiped.
  useEffect(() => {
    if (!value) {
      setIso(DEFAULT_ISO);
      setDialCode(DEFAULT_DIAL_CODE);
      setInputNumber("");
      return;
    }
    const normalizedValue = value.startsWith("+") ? value : `+${value}`;
    let parsed = parsePhoneNumberFromString(normalizedValue);
    if (!parsed?.country) {
      parsed = parsePhoneNumberFromString(value, DEFAULT_ISO);
    }
    if (parsed?.country) {
      setIso(parsed.country);
      setDialCode(`+${parsed.countryCallingCode}`);
      setInputNumber(parsed.nationalNumber as string);
    }
  }, [value]);

  const handleDialChange = (newDialCode: string) => {
    const opt = dialOptions.find((o) => o.dialCode === newDialCode);
    if (opt) setIso(opt.iso);
    setDialCode(newDialCode);
    onChange?.(inputNumber ? `${newDialCode}${inputNumber}` : "");
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const num = e.target.value.replace(/\D/g, "");
    setInputNumber(num);
    onChange?.(num ? `${dialCode}${num}` : "");
  };

  // Normalize to E.164 on blur (strips trunk prefix per country rules)
  const handleNumberBlur = () => {
    const parsed = parsePhoneNumberFromString(`${dialCode}${inputNumber}`);
    if (parsed?.isValid()) {
      setInputNumber(parsed.nationalNumber as string);
      onChange?.(parsed.number);
    }
  };

  return (
    <Space.Compact style={{ width: "100%", display: "flex" }}>
      <Select
        value={dialCode}
        onChange={handleDialChange}
        disabled={disabled}
        showSearch
        style={{ width: 105, flexShrink: 0 }}
        popupMatchSelectWidth={false}
        optionFilterProp="label"
        labelRender={() => (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <ReactCountryFlag countryCode={iso} svg style={{ width: 18, height: 14, borderRadius: 2 }} />
            <span style={{ fontSize: 13 }}>{dialCode}</span>
          </span>
        )}
        options={dialOptions.map(({ iso: i, name, dialCode: dc }) => ({
          value: dc,
          label: `${name} ${dc}`,
          iso: i,
        }))}
        optionRender={(option) => {
          const opt = dialOptions.find((o) => o.dialCode === option.value);
          if (!opt) return option.label;
          return (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ReactCountryFlag countryCode={opt.iso} svg style={{ width: 20, height: 15, borderRadius: 2, flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: 13 }}>{opt.name}</span>
              <span style={{ color: "#aaa", fontSize: 12 }}>{opt.dialCode}</span>
            </span>
          );
        }}
      />
      <Input
        value={inputNumber}
        onChange={handleNumberChange}
        onBlur={handleNumberBlur}
        disabled={disabled}
        placeholder="e.g. 123456789"
        type="tel"
        inputMode="tel"
      />
    </Space.Compact>
  );
};

export default PhoneNumberInput;
